import {
  connect,
  LocalParticipant,
  LocalTrack,
  LocalTrackPublication,
  Logger,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room
} from "twilio-video";
import {BaseListItem, BaseListItemsLoader, ListItemChange, OnListItemsListener} from "../shared/types";
import {JsonObject} from "../shared/json/json-object";
import {JsonProperty} from "../shared/json/json-property";
import {Error} from "@mui/icons-material";
import {JSON_OBJECT} from "../shared/json/helpers";
import {md5_uuid} from "../shared/md5";
import {getMemberAuth} from "../shared/auth";

export const TOKEN_URL = "https://us-central1-playtime-room.cloudfunctions.net/token?identity=";

export enum MeetingType {
  CREATE = "create",
  CONNECT = "connect",
  START = "start",
  END = "end",
}

export enum MeetingEndReason {
  NO_ANSWER,
  HANG_UP,
  TIME_OUT,
  BUSY,
  NAVIGATED_AWAY,
  NO_PERMISSIONS,
}

@JsonObject()
export class Meeting extends BaseListItem {

  @JsonProperty()
  type: MeetingType;
  @JsonProperty()
  time: number; // Last updated.
  started?: number;
  @JsonProperty()
  ended?: number;
  @JsonProperty()
  endReason?: MeetingEndReason;

  static createNew() {
    const meeting = new Meeting(md5_uuid(), getMemberAuth().getMemberId(), Date.now());
    meeting.type = MeetingType.CREATE;
    meeting.time = meeting.created;
    return meeting;
  }

  static updateConnect(meeting: Meeting): Meeting {
    const copy = new Meeting(meeting.id, meeting.creator, meeting.created);
    copy.type = MeetingType.CONNECT;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateStart(meeting: Meeting): Meeting {
    const copy = new Meeting(meeting.id, meeting.creator, meeting.created);
    copy.type = MeetingType.START;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateEnd(meeting: Meeting, endReason: MeetingEndReason): Meeting {
    const copy = new Meeting(meeting.id, meeting.creator, meeting.created);
    copy.type = MeetingType.END;
    copy.ended = Date.now();
    copy.time = copy.ended;
    copy.endReason = endReason;
    return copy;
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export const MEETING_END_REASON_TEXT_FROM_ME = new Map<MeetingEndReason, string>([
    [
      MeetingEndReason.NO_ANSWER, "Did not answer"
    ],
    [
      MeetingEndReason.HANG_UP, "Call ended"
    ],
    [
      MeetingEndReason.TIME_OUT, "Lost connection"
    ],
    [
      MeetingEndReason.BUSY, "On another call",
    ],
    [
      MeetingEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      MeetingEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

export const MEETING_END_REASON_TEXT_FROM_OTHER = new Map<MeetingEndReason, string>([
    [
      MeetingEndReason.NO_ANSWER, "You missed the call"
    ],
    [
      MeetingEndReason.HANG_UP, "Call ended"
    ],
    [
      MeetingEndReason.TIME_OUT, "Lost connection"
    ],
    [
      MeetingEndReason.BUSY, "On another call",
    ],
    [
      MeetingEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      MeetingEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

const IS_MOBILE = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

const DEFAULT_CONNECT_OPTIONS = {
  // Comment this line if you are playing music.
  maxAudioBitrate: 16000,

  // Capture 720p video @ 24 fps.
  video: {height: 720, frameRate: 24, width: 720}
};

export interface RoomListener {

  onConnected(room: Room);

  onAttachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDetachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDisconnected(room: Room);
}

export interface CurrentMeetingListener {
  onMeetingChanged(currentMeeting: Meeting | null);
}

export class Meetings extends BaseListItemsLoader<Meeting> implements OnListItemsListener<Meeting> {

  private static readonly instances = new Map<string, Meetings>();

  private currentMeeting: Meeting;
  private currentMeetingListener: CurrentMeetingListener;
  private roomListener: RoomListener;

  static getInstance(roomId: string, listener: CurrentMeetingListener): Meetings {
    let meetings = this.instances.get(roomId);
    if (!meetings) {
      meetings = new Meetings(roomId);
      this.instances.set(roomId, meetings);
    }
    meetings.currentMeetingListener = listener;
    return meetings;
  }

  private constructor(readonly roomId: string) {
    super({shared: true});
    this.registerObserver(this);
  }

  getCurrentMeeting(): Meeting | null | undefined {
    return this.currentMeeting;
  }

  protected basePath(): string {
    return "meetings/" + this.roomId;
  }

  protected async onAfterItemsLoaded(items: Meeting[]): Promise<Meeting[]> {
    for (const meeting of items) {
      if (!this.currentMeeting && meeting.type !== MeetingType.END) {
        this.currentMeeting = meeting;
      } else {
        if (meeting.type !== MeetingType.END) {
          await this.endOtherMeeting(meeting, MeetingEndReason.NAVIGATED_AWAY);
        }
        await this.deleteListItemById(meeting.id);
      }
    }
    return items;
  }

  onItemChanged(item: Meeting, change: ListItemChange): void {
    if (item.id === this.currentMeeting?.id) {
      this.updateCurrentMeeting(item);
    }
  }

  private updateCurrentMeeting(meeting: Meeting) {
    this.currentMeeting = meeting;
    this.currentMeetingListener?.onMeetingChanged(meeting);
    if (this.currentMeeting?.type === MeetingType.END) {
      this.room?.disconnect();
      this.room = null;
      switch (this.currentMeeting.endReason) {
        case MeetingEndReason.HANG_UP:
          setTimeout(() => {
            this.clearCurrentMeeting();
          }, 1000);
          break;
        case MeetingEndReason.NO_ANSWER:
        case MeetingEndReason.TIME_OUT:
        case MeetingEndReason.BUSY:
        case MeetingEndReason.NAVIGATED_AWAY:
        case MeetingEndReason.NO_PERMISSIONS:
          // Nothing. UI should present a button to clear out current meeting.
          break;
        default:
          this.clearCurrentMeeting();
          break;
      }
    }
  }

  protected deserializeItem(value: any): Meeting {
    return JSON_OBJECT.deserializeObject(value, Meeting);
  }

  protected serializeItem(item: Meeting): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Meeting, item2: Meeting): number {
    return item2.created - item1.created;
  }

  clearCurrentMeeting() {
    this.currentMeeting = null;
    this.currentMeetingListener?.onMeetingChanged(null);
  }

  // CALLER ONLY
  async createMeeting(roomListener: RoomListener) {
    if (this.currentMeeting) {
      throw Error;
    }
    const createMeeting = Meeting.createNew();
    this.currentMeeting = createMeeting;
    await this.addListItem(createMeeting);
    await this.setupAndJoinRoom(roomListener);
    // this.currenMeetingTimeoutId = setTimeout(() => this.endCurrentMeeting(MeetingEndReason.NO_ANSWER), 10 * 60000);
  }

  // CALLEE ONLY
  async connectAndStartCurrentMeeting(roomListener: RoomListener) {
    if (!this.currentMeeting) {
      throw Error;
    }
    const connectMeeting = Meeting.updateConnect(this.currentMeeting);
    this.currentMeeting = connectMeeting;
    await this.addListItem(connectMeeting);

    await this.setupAndJoinRoom(roomListener);

    const startMeeting = Meeting.updateStart(this.currentMeeting);
    this.currentMeeting = startMeeting;
    await this.addListItem(startMeeting);
  }

  private async setupAndJoinRoom(roomListener: RoomListener) {
    const response = await fetch(TOKEN_URL + getMemberAuth().getMemberId());
    const token = await response.text();
    const deviceIds = {
      audio: IS_MOBILE ? null : localStorage.getItem('audioDeviceId'),
      video: IS_MOBILE ? null : localStorage.getItem('videoDeviceId')
    };
    const connectOptions: any = {
      ...DEFAULT_CONNECT_OPTIONS,
    }
    connectOptions.audio = {deviceId: {exact: deviceIds.audio}};
    connectOptions.name = this.roomId;
    connectOptions.video.deviceId = {exact: deviceIds.video};
    await this.joinRoom(token, connectOptions, roomListener);
  }

// CALLER OR CALLEE
  async endCurrentMeeting(reason: MeetingEndReason) {
    if (!this.currentMeeting) {
      throw Error;
    }
    const endMeeting = Meeting.updateEnd(this.currentMeeting, reason);
    this.currentMeeting = endMeeting;
    await this.addListItem(endMeeting);
  }

  private async endOtherMeeting(otherMeeting: Meeting, reason: MeetingEndReason) {
    const endMeeting = Meeting.updateEnd(otherMeeting, reason);
    this.currentMeeting = endMeeting;
    await this.addListItem(endMeeting);
  }

  private attachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.roomListener.onAttachTrack(this.room, track, participant);
  }

  private detachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.roomListener.onDetachTrack(this.room, track, participant);
  }

  private participantConnected(participant: LocalParticipant | RemoteParticipant) {
    // Handle the TrackPublications already published by the Participant.
    participant.tracks.forEach(publication => {
      this.trackPublished(publication, participant);
    });

    // Handle theTrackPublications that will be published by the Participant later.
    participant.on('trackPublished', publication => {
      this.trackPublished(publication, participant);
    });
  }

  private participantDisconnected(participant: LocalParticipant | RemoteParticipant) {
  }

  private trackPublished(publication: LocalTrackPublication | RemoteTrackPublication, participant: LocalParticipant | RemoteParticipant) {
    // If the TrackPublication is already subscribed to, then attach the Track to the DOM.
    if (publication.track) {
      this.attachTrack(publication.track, participant);
    }

    // Once the TrackPublication is subscribed to, attach the Track to the DOM.
    publication.on('subscribed', track => {
      this.attachTrack(track, participant);
    });

    // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
    publication.on('unsubscribed', track => {
      this.detachTrack(track, participant);
    });
  }

  private room: Room | null;

  private async joinRoom(token: string, connectOptions: any, listener: RoomListener) {
    this.roomListener = listener;
    // Comment the next two lines to disable verbose logging.
    const logger = Logger.getLogger('twilio-video');
    logger.setLevel('error');

    // Join to the Room with the given AccessToken and ConnectOptions.
    const room = await connect(token, connectOptions);
    this.room = room;
    this.roomListener.onConnected(this.room);

    // Save the LocalVideoTrack.
    const localVideoTrack = Array.from(room.localParticipant.videoTracks.values())[0].track;

    // Make the Room available in the JavaScript console for debugging.
    // window.room = room;

    // Handle the LocalParticipant's media.
    this.participantConnected(room.localParticipant);

    // Subscribe to the media published by RemoteParticipants already in the Room.
    room.participants.forEach(participant => {
      this.participantConnected(participant);
    });

    // Subscribe to the media published by RemoteParticipants joining the Room later.
    room.on('participantConnected', participant => {
      this.participantConnected(participant);
    });

    // Handle a disconnected RemoteParticipant.
    room.on('participantDisconnected', participant => {
      this.participantDisconnected(participant);
    });

    room.once('disconnected', (room, error) => {
      // Stop the LocalVideoTrack.
      localVideoTrack.stop();

      // Handle the disconnected LocalParticipant.
      this.participantDisconnected(room.localParticipant);

      // Handle the disconnected RemoteParticipants.
      room.participants.forEach(participant => {
        this.participantDisconnected(participant);
      });

      this.roomListener.onDisconnected(this.room);
    });
  }

  getOtherParticipantCount(): number {
    return this.room?.participants.size || 0;
  }
}